/**
 * Main entry point for Audelio application - Streamlined Version
 * ES6 Module System Implementation - Code Extracted to Modules
 * Main entry point has been refactored from ~4200 lines to a thin glue layer; core logic extracted to focused modules.
 * Build: v2.0.0
 */

// Initialize error monitoring and error boundary FIRST
import { initSentry } from './monitoring/sentry.js';
import { initErrorBoundary } from './core/error-boundary.js';

// Initialize Sentry with configuration from environment variables
const sentryConfig = {
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  environment: import.meta.env.MODE || 'production',
  release: import.meta.env.VITE_APP_VERSION || '2.0.0'
};
initSentry(sentryConfig);

// Initialize global error boundary
initErrorBoundary();

// Import core modules
import { appFlags } from './config/feature-flags.js';
import { EVENT_TYPES } from './core/event-types.js';
import logger from './core/logger.js';
import EventLogger, { logEvent } from './core/event-logger.js';
import appState from './core/app-state.js';
import router from './core/router.js';

// Import types
import type { Clip } from './types/clip.js';
import type { ViewMode } from './types/app-state.js';

// Import services
import * as ProgressService from './services/progress-service.js';
import { registerPerformanceCallbacks } from './services/performance-service.js';

// Import UI modules
import { savePreferences, registerSettingsCallbacks } from './ui/settings-modal.js';
import { showToast, showConfirmModal, updateFooter } from './ui/ui-helpers.js';
import { renderReportIssueButton } from './ui/report-issue-dialog.js';
import { renderClip } from './ui/clip-renderer.js';

// Import player modules
import * as TranscriptModule from './modules/player/transcript.js';
import { resetClipState, handleNextClip as handleNextClipController } from './modules/player/clip-controller.js';
import { trackDepthSeek } from './modules/player/depth-metrics.js';

// Import question modules
import * as QuestionManager from './modules/questions/question-manager.js';
import { renderSequenceQuestion } from './modules/questions/sequence-question.js';
import { renderClozeQuestion } from './modules/questions/cloze-question.js';
import { registerSpecializedRenderers } from './modules/questions/question-coordinator.js';

// Import library and home modules
import { libraryView, renderLibraryView } from './modules/library/library-view.js';
import { generateDailySet, getTodayDate } from './modules/daily-set/daily-set-generator.js';
import { renderHomeView as homeViewRender, registerHomeCallbacks } from './modules/home/home-view.js';
import { registerOnboardingCallbacks } from './modules/home/onboarding.js';

// Import admin module
import { initAdminViews } from './modules/admin/admin-view-manager.js';

// Import bootstrap and event handlers
import { initApp as initializeApp, applyTouchModeIfNeeded } from './core/bootstrap.js';
import { setupAutoAdvanceCancellation } from './ui/event-handlers.js';

// Set app state reference in event logger
EventLogger.setAppState(appState);

// ========================================================================
// Glue Functions - Minimal wrappers for cross-module communication
// ========================================================================

/**
 * Switch application mode (wrapper for router)
 */
export function switchMode(mode: ViewMode): void {
  router.switchMode(mode);
}

/**
 * Set the current clip by index
 */
export function setCurrentClip(index: number): void {
  appState.currentClipIdx = index;
  appState.currentQuestionIndex = 0;
  const clip = appState.clips[appState.currentClipIdx];
  
  if (clip && clip.id) {
    appState.activeClipId = clip.id;
  }
  
  if (clip) {
    renderClip(clip);
  }
  switchMode('exercise');
}

/**
 * Handle navigation to next clip (wrapper)
 */
export function handleNextClip(): void {
  handleNextClipController(
    (idx: number) => renderClip(idx),
    (mode: string) => switchMode(mode as ViewMode)
  );
}

/**
 * Wrapper for router.updateHeaderActions
 */
function updateHeaderActions(): void {
  router.updateHeaderActions();
}

/**
 * Expose updateHeaderActions globally for clip-renderer
 */
declare global {
  interface Window {
    _updateHeaderActions: () => void;
    seekToTimestamp: (sec: number, options?: { source?: string; sentenceIndex?: number }) => void;
    saveTranscriptVisibility: (isOpen: boolean) => void;
    switchMode: (mode: ViewMode) => void;
    testTranscriptClick: (sentenceIndex: number) => boolean;
    backfillAllSentenceEndTimestamps: () => string;
  }
}

window._updateHeaderActions = updateHeaderActions;

/**
 * Check if a clip is completed (wrapper for ProgressService)
 */
export function isClipCompleted(clipId: string): boolean {
  return ProgressService.isClipCompleted(clipId);
}

/**
 * Update progress UI elements
 */
export function updateProgressUI(): void {
  QuestionManager.updateQuestionProgressBar();
}

/**
 * Render home view (wrapper for home module)
 */
function renderHomeView(): void {
  homeViewRender();
}

/**
 * Render admin view (wrapper for admin module)
 */
function renderAdminView(): void {
  const adminContainer = document.getElementById('admin-container');
  if (!adminContainer) {
    logger.error('Admin container not found');
    return;
  }
  initAdminViews(adminContainer);
}

// ========================================================================
// Shared Functions and Utilities
// ========================================================================

/**
 * Seek audio to a specific timestamp
 */
function seekToTimestamp(sec: number, options: { source?: string; sentenceIndex?: number } = {}): void {
  logger.debug('Seeking to timestamp', { sec, options });
  
  const audio = document.querySelector('audio');
  if (!audio) {
    logger.error('No audio element found in page');
    return;
  }
  
  logger.debug('Audio state before seek', {
    paused: audio.paused,
    currentTime: audio.currentTime,
    duration: audio.duration,
    readyState: audio.readyState
  });
  
  try {
    if (typeof sec !== 'number' || isNaN(sec)) {
      logger.warn('Invalid timestamp', { sec });
      sec = 0;
    }
    
    const safeTime = Math.max(0, Math.min(sec || 0, audio.duration || 9999));
    logger.debug('Setting audio time to', { safeTime });
    
    const previousTime = audio.currentTime;
    audio.currentTime = safeTime;
    
    // Track seek for depth metrics
    trackDepthSeek(audio, previousTime, safeTime);
    
    // Reset auto-follow state if seeking to beginning
    if (safeTime <= 2.0) {
      const transcriptContainer = document.getElementById('transcriptContainer');
      if (transcriptContainer) {
        const resetEvent = new CustomEvent('resetAutoFollow');
        transcriptContainer.dispatchEvent(resetEvent);
      }
    }
    
    // Log analytics event for replay seeks
    if (options && options.source === 'replay') {
      const replaySeekEvent = EVENT_TYPES['TRANSCRIPT_SENTENCE_REPLAY_SEEK'];
      if (replaySeekEvent) {
        logEvent(replaySeekEvent, {
          timestamp: safeTime,
          sentenceIndex: options.sentenceIndex,
          previousTime: audio.currentTime
        });
      }
      
      if (appFlags.replayInstrumentation && appState.activeClipId) {
        const clipId = appState.activeClipId;
        
        if (!appState.clipReplayCount.has(clipId)) {
          appState.clipReplayCount.set(clipId, {
            count: 0,
            firstTimestamp: new Date().toISOString(),
            lastTimestamp: ''
          });
        }
        
        const replayData = appState.clipReplayCount.get(clipId);
        if (replayData) {
          replayData.count++;
          replayData.lastTimestamp = new Date().toISOString();
        }
        
        const replayActionEvent = EVENT_TYPES['REPLAY_ACTION'];
        if (replayActionEvent) {
          logEvent(replayActionEvent, {
            clipId: clipId,
            atTime: safeTime,
            sentenceIndex: options.sentenceIndex,
            replayCountForClip: replayData?.count
          });
        }
      }
    }
    
    // Trigger sentence tracker for immediate highlighting
    const audioWithTracker = audio as HTMLAudioElement & { _sentenceTracker?: () => void };
    if (audioWithTracker._sentenceTracker) {
      audioWithTracker._sentenceTracker();
      
      if (document.activeElement && document.activeElement.classList && 
          document.activeElement.classList.contains('t-sentence')) {
        (document.activeElement as HTMLElement).blur();
      }
    }
    
    // Try to play audio
    setTimeout(() => {
      try {
        audio.play();
      } catch (e) {
        logger.error('Delayed play failed', e);
      }
    }, 50);
    
    try {
      audio.play();
    } catch (e) {
      const playButton = document.getElementById('playPauseBtn');
      if (playButton && audio.paused) {
        logger.debug('Triggering play button click as fallback');
        playButton.click();
      }
    }
  } catch (e) {
    logger.error('Critical error in seekToTimestamp', e);
  }
}

// Expose functions for transcript module and other global access
window.seekToTimestamp = seekToTimestamp;
window.saveTranscriptVisibility = ProgressService.saveTranscriptVisibility;
window.switchMode = switchMode;

// Debug utility function for browser console testing
window.testTranscriptClick = function(sentenceIndex: number): boolean {
  logger.debug('Running transcript click test', { sentenceIndex });
  
  const sentences = document.querySelectorAll('.t-sentence');
  if (!sentences.length) {
    logger.error('No transcript sentences found');
    return false;
  }
  
  logger.debug('Found transcript sentences', { count: sentences.length });
  
  const sentence = (sentences[sentenceIndex] || sentences[0]) as HTMLElement;
  logger.debug('Selected sentence', { sentence });
  
  const timestamp = parseFloat(sentence.dataset['start'] || '0');
  logger.debug('Sentence timestamp', { timestamp });
  
  const audio = document.querySelector('audio');
  if (!audio) {
    logger.error('No audio element found');
    return false;
  }
  
  logger.debug('Current audio state', {
    paused: audio.paused,
    currentTime: audio.currentTime,
    duration: audio.duration
  });
  
  try {
    audio.currentTime = timestamp;
    audio.play();
    logger.debug('Audio play triggered');
    return true;
  } catch (e) {
    logger.error('Audio play failed', e);
    return false;
  }
};

// Utility function to backfill end timestamps for all clips
window.backfillAllSentenceEndTimestamps = function(): string {
  if (!appState.clips || !Array.isArray(appState.clips)) {
    logger.error('No clips data available');
    return 'No clips data available';
  }
  
  let updatedCount = 0;
  
  appState.clips.forEach((clip: Clip) => {
    if (clip.sentences && clip.sentences.length > 0) {
      const needsBackfill = clip.sentences.some(s => s.end === undefined);
      
      if (needsBackfill) {
        Object.assign(clip, TranscriptModule.backfillSentenceEndTimestamps(clip));
        updatedCount++;
      }
    }
  });
  
  logger.info('Updated end timestamps', { count: updatedCount });
  return `Updated ${updatedCount} clips with end timestamps`;
};

// ========================================================================
// Callback Registration and Initialization
// ========================================================================

/**
 * Register all module callbacks
 */
function registerAllCallbacks(): void {
  // Register specialized question renderers
  registerSpecializedRenderers({
    renderSequenceQuestion,
    renderClozeQuestion
  });
  
  // Register UI callbacks for question manager
  QuestionManager.registerUICallbacks({
    handleNextClip,
    renderReportIssueButton
  });
  
  // Register library view callbacks
  libraryView.registerCallbacks({
    resetClipState,
    renderClip,
    switchMode: (mode: string) => switchMode(mode as ViewMode),
    savePreferences
  });
  
  // Register home view callbacks
  registerHomeCallbacks({
    isClipCompleted: ProgressService.isClipCompleted,
    setCurrentClip,
    switchMode
  });
  
  // Register settings modal callbacks
  registerSettingsCallbacks({
    showToast: (message: string, type?: string, duration?: number) => 
      showToast(message, type as 'info' | 'success' | 'error' | undefined, duration),
    showConfirmModal,
    updateFooter,
    renderLibraryView,
    renderHomeView,
    generateDailySet: (mode: string) => 
      generateDailySet(mode as 'auto' | 'manualShuffle' | 'onboarding' | undefined),
    getTodayDate
  });
  
  // Register onboarding modal callbacks
  registerOnboardingCallbacks({
    savePreferences,
    generateDailySet,
    renderLibraryView
  });
  
  // Register performance service callbacks
  registerPerformanceCallbacks({
    switchMode: (mode: string) => switchMode(mode as ViewMode),
    resetClipState,
    renderClip
  });
}

/**
 * Initialize the application on DOM ready
 */
document.addEventListener('DOMContentLoaded', () => {
  applyTouchModeIfNeeded();
  
  // Register all callbacks first
  registerAllCallbacks();
  
  // Setup auto-advance cancellation
  setupAutoAdvanceCancellation();
  
  // Initialize the app with bootstrap
  initializeApp(
    renderHomeView,
    renderLibraryView,
    renderAdminView,
    (mode: string) => switchMode(mode as ViewMode),
    setCurrentClip,
    updateProgressUI,
    {
      resetClipState,
      renderClip,
      savePreferences
    }
  );
});

// ========================================================================
// Re-exports for backward compatibility
// ========================================================================

export { renderSessionSummary } from './services/performance-service.js';
export { calculateClipPerformance } from './services/performance-service.js';
